import type { AiCapabilityPage } from "../schema.js";
import { ALL_ROLES, LIVE, SLOT_FUND, SLOT_GROUP } from "./_shared.js";

/**
 * `READ-FUND-MONEY` (@UC0205) — đọc TIỀN CỦA QUỸ: số dư · giao dịch gần đây · danh sách khoản chi. Tier `read`: không phiếu,
 * chạy ngay. Số CHÈN SAU LLM từ fact block (`@UC0154`). Executor `fundRead.money` là **dispatcher** theo ô `chiSo`, nối tới
 * ĐÚNG hai cửa của màn Quỹ: `FundDashboardService.getDashboard` (số dư + thu/chi kỳ) · `TransactionsService.listTransactions`
 * (giao dịch gần đây, **và** danh sách chi = CÙNG hàm đó với `type: "expense"`). Cùng hàm với màn Quỹ, không truy vấn riêng.
 *
 * 🔴 Ghi chú nháp của 0013 từng nêu `ExpenseReadService.listForFund` cho "khoản chi" — US-AIV2-0044 **KHÔNG** dùng nó, có chủ đích:
 * trang này nói về TIỀN CỦA QUỸ, mà một `expense` chưa duyệt/chưa post **chưa rời quỹ đồng nào** (trộn *đã chi* với *định chi*
 * là lớp lỗi §3.7); và một cửa thay vì hai nghĩa là một luật redaction, một luật `from`/`to`, một chỗ đọc cờ `isReversal`/
 * `isReversed` (nhãn "bút toán đảo"/"đã đảo" — bảng `expenses` không có). Sửa dòng này 18/09 vì nó khai một cửa KHÔNG được gọi.
 */
export const READ_FUND_MONEY = {
  id: "READ-FUND-MONEY",
  uc: "@UC0205",
  tier: "read",
  status: LIVE,
  roles: ALL_ROLES,
  flags: [],
  slots: [
    // 🔴 US-AIV2-0112 SLOT-GROUP (@UC0300, follow-up H01 #1037): ô nhóm ĐẦU (bậc phạm vi) = đường vào khi phiên chưa có nhóm; redaction
    // (§3.8 `'•••'`) vẫn theo vai THẬT ở nhóm được chốt — cổng `assertCanStart` trả vai của đúng nhóm đó cho binding.
    SLOT_GROUP,
    SLOT_FUND,
    {
      id: "chiSo",
      type: "enum",
      required: true,
      ask: "Bạn muốn xem gì: số dư, giao dịch gần đây, hay các khoản chi?",
      clarify: "Bạn muốn xem số dư, giao dịch gần đây, hay danh sách khoản chi?",
      choices: ["số dư", "giao dịch gần đây", "danh sách chi"],
      /*
       * 🔴 US-AIV2-0111 C10 (@UC0205) — cách người Việt HỎI từng chỉ số. Đo 28/09: LLM trích đúng "còn bao nhiêu tiền",
       * "tiêu vào việc gì", "chi bao nhiêu" vào ô này mà không cụm nào chứa chữ một lựa chọn ⇒ hỏi lại. Khớp theo TỪ, bỏ dấu;
       * cụm dài nhất thắng ("chi bao nhiêu" ⇒ số dư, vì câu trả lời số dư có sẵn tổng chi của tháng; "chi" trần ⇒ danh sách chi).
       */
      synonyms: {
        "số dư": ["còn bao nhiêu", "còn nhiêu", "còn lại bao nhiêu", "còn bao nhiêu tiền", "còn đủ", "còn tiền", "còn lại", "chi bao nhiêu", "chi hết bao nhiêu", "tiêu hết bao nhiêu", "thu chi", "tổng quan"],
        "giao dịch gần đây": ["lịch sử", "biến động", "mới nhất", "vừa có", "gần đây", "thu chi gần đây"],
        "danh sách chi": ["chi", "đã chi", "chi gì", "chi những gì", "chi những khoản nào", "chi vào", "khoản chi", "sổ chi", "tiêu vào", "tiêu vào việc gì", "tiêu gì", "tiêu những gì", "đã tiêu"],
      },
      // Mơ hồ ("xem quỹ", "bao nhiêu") ⇒ trả số dư: câu trả lời có cả số dư LẪN thu/chi của tháng — giàu thông tin nhất, không hỏi.
      fallback: "số dư",
    },
    { id: "ky", type: "date", required: false, ask: "Kỳ nào? (bỏ trống = tháng này)" },
  ],
  llm: {
    /*
     * 🔴 US-AIV2-0112 lô Sổ 3 (@UC0300, hồi quy lộ ở probe SAU #1059): SLOT-GROUP đưa trang này vào tập được phép của người ≥2 nhóm chưa chọn
     * nhóm ⇒ BM25 trên tập nhỏ đó kéo "tháng này mình phải đóng gì" về đây (3/3). Câu ranh giới cuối trả nó về READ-PEOPLE-MINE 3/3 ở cả hai
     * tập. Bản đầu "Mình phải đóng gì ⇒ …" làm EXP-F-NOALLOC#17 "tien dien thang nay 800k quy tra" lật sang trang này (eval amount 35/36; probe 4/5)
     * ⇒ đổi "Khoản mình phải đóng ⇒ …" (5/5 · 3/3). Đo lại (`_runs/US-AIV2-0112/SO-LO3/probe-fm.json` + `probe-noalloc17.json`, `_groupless`) nếu sửa.
     */
    description: "Xem tiền của quỹ: còn bao nhiêu, vừa có giao dịch gì, tháng này chi những khoản nào. Khoản mình phải đóng ⇒ READ-PEOPLE-MINE.",
    examples: [
      "quỹ còn bao nhiêu?",
      "quy con bao nhieu",
      "tháng này chi những khoản nào?",
      "thang nay chi nhung khoan nao",
      "giao dịch gần đây của quỹ lớp",
      "giao dich gan day",
      "số dư",
      "còn bao nhiêu tiền",
      "xem sổ chi tháng 9",
      "tiền quỹ giờ sao rồi",
      "chi gì tháng này",
      "số dư quỹ",
      // ——— 0105: thêm cho đủ 40 ———
      "so du",
      "con bao nhieu tien",
      "xem so chi thang 9",
      "tien quy gio sao roi",
      "chi gi thang nay",
      "so du quy",
      "quỹ lớp còn lại bao nhiêu?",
      "quy lop con lai bao nhieu",
      "cho xem số dư quỹ đội bóng",
      "cho xem so du quy doi bong",
      "tháng trước quỹ chi hết bao nhiêu",
      "thang truoc quy chi het bao nhieu",
      "xem các giao dịch mới nhất của quỹ",
      "xem cac giao dich moi nhat cua quy",
      "quỹ vừa có biến động gì không",
      "quy vua co bien dong gi khong",
      "liệt kê khoản chi quý này",
      "liet ke khoan chi quy nay",
      "tiền trong quỹ còn nhiêu",
      "tien trong quy con nhieu",
      "cho mình xem sổ quỹ",
      "cho minh xem so quy",
      "quỹ thu chi thế nào rồi",
      "quy thu chi the nao roi",
      "kiểm tra số dư giúp tôi",
      "kiem tra so du giup toi",
      "xem lịch sử giao dịch quỹ",
      "xem lich su giao dich quy",
      "quỹ đã tiêu những gì",
      "quy da tieu nhung gi",
    ],
    // AI-INTERP M12 (QĐ I12): câu THỬ — KHÔNG vào prompt/BM25; viết đời thường, không chép ví dụ (validator HELD_OUT_IS_EXAMPLE).
    heldOutExamples: [
      "quy con nhieu tien ko chi Hai",
      "tháng này xài hết bao nhiêu rồi ạ",
      "coi giup tui so du quy doi bong",
      "dạo này quỹ có chi gì không em",
      "quy lop gio con dc bao nhieu",
      "cho cháu xem quỹ thu chi tháng vừa rồi",
    ],
  },
  executor: "fundRead.money",
  /** `@UC0216` — cửa mobile trang này chịu trách nhiệm: danh sách quỹ + số dư; chi tiết giao dịch đọc qua cùng service. */
  endpoints: [
    "GET /funds",
  ],
  facts: ["tenQuy", "soDu", "tomTat"],
  templates: {
    reply: "Quỹ {tenQuy}: {tomTat} (số dư hiện tại {soDu}).",
  },
  /**
   * US-AIV2-tourguide-0005 (@UC0364) — kết quả của binding: `shown` (số dư / danh sách có dòng) · `empty` (quỹ chưa có giao dịch / khoản chi nào)
   * · `hidden` (số bị che theo cài đặt minh bạch — người xem không thấy tiền thì không mời chia sẻ minh bạch).
   */
  journey: {
    outcomes: {
      shown: { next: ["READ-COLLECTIONS", "SHARE-TRANSPARENCY"] },
      empty: { next: ["READ-COLLECTIONS", "SHARE-TRANSPARENCY"] },
      hidden: { next: ["READ-COLLECTIONS"] },
    },
  },
  help: { title: "Xem số dư quỹ", tryNow: "quỹ còn bao nhiêu?" },
} satisfies AiCapabilityPage;
